import type { OverlayManager } from "../overlay/OverlayManager";
import type { TextOverlay, LineOverlay, ArcOverlay } from "../overlay/types";
import { OverlayType } from "../overlay/types";
import {
  MEASUREMENT_LABEL_STYLE,
  MEASUREMENT_GUIDE_STYLE,
} from "../draggers/previewStyles";
import type { OverlayColors } from "./OverlayTheme";
import { getOverlayTheme } from "./OverlayTheme";

/** Font size for measurement labels */
const LABEL_FONT_SIZE = 12;

export class MeasurementLabels {
  private labels = new Map<string, string>(); // key -> overlay ID
  private guides = new Map<string, string>(); // key -> overlay ID
  private theme: OverlayColors;

  constructor(
    private overlayManager: OverlayManager,
    backgroundColor: number = 0xffffff,
  ) {
    this.theme = getOverlayTheme(backgroundColor);
  }

  /**
   * Show or update a centered label at the given screen position.
   */
  setLabel(key: string, x: number, y: number, text: string): void {
    const existingId = this.labels.get(key);
    if (existingId) {
      this.overlayManager.update(existingId, {
        x,
        y,
        text,
      } as Partial<TextOverlay>);
    } else {
      const id = this.overlayManager.add({
        type: OverlayType.Text,
        x,
        y,
        text,
        fontSize: LABEL_FONT_SIZE,
        textAlign: "center" as CanvasTextAlign,
        textBaseline: "middle" as CanvasTextBaseline,
        backgroundColor: this.theme.labelBackground,
        padding: 4,
        style: { ...MEASUREMENT_LABEL_STYLE, fillColor: this.theme.labelText },
      } as Omit<TextOverlay, "id">);
      this.labels.set(key, id);
    }
  }

  /**
   * Show or update a guide line at the given screen coordinates.
   */
  setLine(key: string, x1: number, y1: number, x2: number, y2: number): void {
    const existingId = this.guides.get(key);
    if (existingId) {
      this.overlayManager.update(existingId, {
        x1,
        y1,
        x2,
        y2,
      } as Partial<LineOverlay>);
    } else {
      const id = this.overlayManager.add({
        type: OverlayType.Line,
        x1,
        y1,
        x2,
        y2,
        style: {
          ...MEASUREMENT_GUIDE_STYLE,
          strokeColor: this.theme.guideLineColor,
        },
      } as Omit<LineOverlay, "id">);
      this.guides.set(key, id);
    }
  }

  /**
   * Show or update a guide arc at the given screen coordinates.
   * Angles are in radians.
   */
  setArc(
    key: string,
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    counterClockwise?: boolean,
  ): void {
    const existingId = this.guides.get(key);
    if (existingId) {
      this.overlayManager.update(existingId, {
        cx,
        cy,
        radius,
        startAngle,
        endAngle,
        counterClockwise,
      } as Partial<ArcOverlay>);
    } else {
      const id = this.overlayManager.add({
        type: OverlayType.Arc,
        cx,
        cy,
        radius,
        startAngle,
        endAngle,
        counterClockwise,
        style: {
          ...MEASUREMENT_GUIDE_STYLE,
          strokeColor: this.theme.guideLineColor,
        },
      } as Omit<ArcOverlay, "id">);
      this.guides.set(key, id);
    }
  }

  /**
   * Remove a specific label.
   */
  removeLabel(key: string): void {
    const id = this.labels.get(key);
    if (id) {
      this.overlayManager.remove(id);
      this.labels.delete(key);
    }
  }

  /**
   * Remove a specific guide (line or arc).
   */
  removeGuide(key: string): void {
    const id = this.guides.get(key);
    if (id) {
      this.overlayManager.remove(id);
      this.guides.delete(key);
    }
  }

  /**
   * Remove all labels and guides.
   */
  clear(): void {
    for (const id of this.labels.values()) {
      this.overlayManager.remove(id);
    }
    this.labels.clear();
    for (const id of this.guides.values()) {
      this.overlayManager.remove(id);
    }
    this.guides.clear();
  }

  /**
   * Format a world-unit distance for display.
   * Produces clean numbers: 10, 3.14, 0.5 (no trailing zeros).
   */
  static formatDistance(value: number): string {
    return parseFloat(value.toFixed(2)).toString();
  }

  /**
   * Format an angle in degrees for display.
   * Produces values like: 0°, 45°, 90.5°, 270°
   */
  static formatAngle(degrees: number): string {
    return parseFloat(degrees.toFixed(1)).toString() + "°";
  }
}
